// Admin card (Settings → Integrations): switch Calendar Sync on/off for the whole
// company. When on, each employee can connect their OWN Google/Outlook from their
// My Calendar page so their approved leave syncs to their personal calendar.
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Switch } from '@/features/shared/components/ui/switch';
import { CalendarCheck, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getCalendarAdminSettings,
  setCalendarAdminSettings,
  getOrgMeetingStatus,
  setGoogleOrgCredential,
  setMicrosoftOrgCredential,
  verifyOrgCredential,
  deleteOrgCredential,
  type CalendarAdminSettings,
  type MeetingProvider,
  type OrgCredentialStatus,
} from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';

const PROVIDER_LABEL: Record<string, string> = { google: 'Google Calendar', microsoft: 'Outlook' };

export const CalendarSyncSettingsCard: React.FC = () => {
  const [settings, setSettings] = useState<CalendarAdminSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getCalendarAdminSettings()
      .then(setSettings)
      .catch(() => setSettings(null))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (enabled: boolean) => {
    if (!settings) return;
    setSaving(true);
    // optimistic
    setSettings({ ...settings, enabled });
    try {
      const updated = await setCalendarAdminSettings(enabled);
      setSettings(updated);
    } catch {
      setSettings({ ...settings, enabled: !enabled }); // revert
    } finally {
      setSaving(false);
    }
  };

  const setProvider = async (meetingProvider: MeetingProvider) => {
    if (!settings) return;
    const prev = settings.meeting_provider ?? 'none';
    setSaving(true);
    setSettings({ ...settings, meeting_provider: meetingProvider }); // optimistic
    try {
      const updated = await setCalendarAdminSettings(!!settings.enabled, meetingProvider);
      setSettings(updated);
    } catch {
      setSettings({ ...settings, meeting_provider: prev }); // revert
    } finally {
      setSaving(false);
    }
  };

  const available = !!settings?.platform_available;
  const providerNames = (settings?.providers || []).map((p) => PROVIDER_LABEL[p] || p).join(' & ');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarCheck className="w-5 h-5 text-orange-600" /> Calendar Sync
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Let employees sync their approved time off to their own Google Calendar or Outlook.
          When you turn this on, each person can connect their personal calendar from their
          <strong> My Calendar</strong> page — they each control their own connection.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : !available ? (
          <div className="rounded-lg border border-warning bg-warning-soft p-3 text-sm text-warning-strong">
            Calendar Sync isn’t available yet — the platform connection (Google / Microsoft) is
            still being set up. You’ll be able to switch it on here once it’s ready.
          </div>
        ) : (
          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                Enable calendar sync for everyone
              </p>
              <p className="text-xs text-muted-foreground">
                {settings?.enabled
                  ? `On — employees can connect ${providerNames || 'their calendar'} from My Calendar.`
                  : 'Off — employees won’t see the connect option.'}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {saving ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : null}
              <Switch
                checked={!!settings?.enabled}
                onCheckedChange={toggle}
                disabled={saving}
                aria-label="Enable calendar sync"
              />
            </div>
          </div>
        )}

        {available && settings?.enabled && (
          <div className="rounded-lg border border-border p-4 space-y-2">
            <p className="text-sm font-medium text-foreground">Standard meeting links</p>
            <p className="text-xs text-muted-foreground">
              When someone schedules a meeting, offer a video link from your company's standard
              tool. Organizers connect their own calendar once; the link is generated
              automatically after that.
            </p>
            <div className="flex flex-wrap gap-2 pt-1" role="radiogroup" aria-label="Standard meeting provider">
              {([
                ['none', 'None'],
                ['google', 'Google Meet'],
                ['microsoft', 'Microsoft Teams'],
              ] as [MeetingProvider, string][]).map(([value, label]) => {
                const active = (settings.meeting_provider ?? 'none') === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={saving}
                    onClick={() => setProvider(value)}
                    className={`h-8 px-3 rounded-lg border text-sm font-medium ${
                      active
                        ? 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-500/[0.12] dark:text-orange-300'
                        : 'border-border bg-card text-foreground hover:bg-muted'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {available && settings?.enabled && (settings.meeting_provider ?? 'none') !== 'none' && (
          <OrgCredentialPanel provider={settings.meeting_provider as 'google' | 'microsoft'} />
        )}
      </CardContent>
    </Card>
  );
};

// --- Org credentials (Phase 2): one company account, links for everyone -----

const OrgCredentialPanel: React.FC<{ provider: 'google' | 'microsoft' }> = ({ provider }) => {
  const [status, setStatus] = useState<OrgCredentialStatus | null>(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => getOrgMeetingStatus(provider).then(setStatus).catch(() => setStatus(null));
  useEffect(() => { setInput(''); load(); }, [provider]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!input.trim()) return;
    setBusy(true);
    try {
      const s = provider === 'google'
        ? await setGoogleOrgCredential(input.trim())
        : await setMicrosoftOrgCredential(input.trim());
      setStatus(s);
      setInput('');
      toast.success('Organization credential saved — run Verify to confirm it works.');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not save the credential'), { duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const r = await verifyOrgCredential(provider);
      if (r.ok) toast.success('Verified — meeting links work for your organization.');
      else toast.error(r.error || 'Verification failed', { duration: 8000 });
      load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteOrgCredential(provider);
      toast.success('Organization credential removed.');
      load();
    } finally {
      setBusy(false);
    }
  };

  const chip =
    status?.status === 'verified' ? ['Verified', 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/[0.16] dark:text-emerald-300'] :
    status?.status === 'error' ? ['Error', 'bg-red-100 text-red-700 dark:bg-red-500/[0.18] dark:text-red-300'] :
    status?.configured ? ['Configured', 'bg-amber-100 text-amber-700 dark:bg-amber-500/[0.16] dark:text-amber-300'] :
    ['Not set up', 'bg-muted text-muted-foreground'];

  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-foreground m-0">
          Organization account {provider === 'google' ? '(Google Workspace)' : '(Microsoft 365)'}
        </p>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${chip[1]}`}>{chip[0]}</span>
      </div>
      <p className="text-xs text-muted-foreground m-0">
        {provider === 'google'
          ? 'Paste the service-account key file your Workspace admin created with domain-wide delegation for Meet. Every organizer then gets Meet links with no personal connection.'
          : 'Enter your Microsoft directory (tenant) ID. Your M365 admin then opens the consent link once, and every organizer gets Teams links with no personal connection.'}
      </p>

      {provider === 'microsoft' && status?.platform_app_configured === false && (
        <p className="text-xs text-warning-strong bg-warning-soft border border-warning rounded p-2 m-0">
          The platform's Microsoft Graph app isn't configured yet (MS_ORG_CLIENT_ID) — contact KaziPeople support.
        </p>
      )}

      {provider === 'google' ? (
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder='{"type": "service_account", "client_email": "…", "private_key": "…"}'
          rows={3}
          className="w-full px-3 py-2 rounded-lg border border-border bg-card text-foreground text-xs font-mono focus:outline-none focus:ring-2 focus:ring-orange-300"
        />
      ) : (
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="e.g. 72f988bf-86f1-41af-91ab-2d7cd011db47"
          className="w-full h-9 px-3 rounded-lg border border-border bg-card text-foreground text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-300"
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={save} disabled={busy || !input.trim()}
          className="h-8 px-3 rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 text-sm font-medium">
          Save
        </button>
        {status?.configured && (
          <>
            <button type="button" onClick={verify} disabled={busy}
              className="h-8 px-3 rounded-lg border border-border text-foreground hover:bg-muted disabled:opacity-50 text-sm font-medium">
              Verify
            </button>
            {provider === 'microsoft' && status.admin_consent_url && (
              <a href={status.admin_consent_url} target="_blank" rel="noopener noreferrer"
                className="h-8 px-3 inline-flex items-center rounded-lg border border-border text-foreground hover:bg-muted text-sm font-medium">
                Open admin consent
              </a>
            )}
            <button type="button" onClick={remove} disabled={busy}
              className="h-8 px-3 rounded-lg border border-border text-red-600 hover:bg-muted disabled:opacity-50 text-sm font-medium">
              Remove
            </button>
          </>
        )}
        {busy && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      {status?.status === 'error' && status.last_error && (
        <p className="text-xs text-red-600 dark:text-red-400 m-0 break-all">Last error: {status.last_error}</p>
      )}

      {provider === 'microsoft' && status?.configured && (
        <details className="text-xs">
          <summary className="cursor-pointer font-medium text-foreground select-none">
            For your IT team — one required Teams policy
          </summary>
          <div className="mt-2 space-y-2">
            <p className="text-muted-foreground m-0">
              After admin consent, a Teams administrator must allow the KaziPeople app to create
              meetings on behalf of your users (Microsoft requires this explicit policy). Run once
              in Teams PowerShell — takes up to ~30 minutes to apply:
            </p>
            <div className="relative">
              <pre className="rounded-lg bg-muted p-3 overflow-x-auto text-[11px] leading-relaxed font-mono text-foreground">{MS_ACCESS_POLICY_SCRIPT}</pre>
              <button
                type="button"
                onClick={() => { navigator.clipboard?.writeText(MS_ACCESS_POLICY_SCRIPT); toast.success('Copied'); }}
                className="absolute top-2 right-2 h-6 px-2 rounded border border-border bg-card text-foreground hover:bg-muted text-[11px] font-medium"
              >
                Copy
              </button>
            </div>
            <p className="text-muted-foreground m-0">
              To limit it to specific people instead of everyone, replace the last line with{' '}
              <code className="font-mono">Grant-CsApplicationAccessPolicy -PolicyName "KaziPeople-Meeting-Links" -Identity user@yourcompany.com</code>{' '}
              per user. Then come back here and press Verify.
            </p>
          </div>
        </details>
      )}
    </div>
  );
};

// The KaziPeople Graph app's public client id — required by the customer's
// Teams application access policy (client ids are not secrets).
const KAZIPEOPLE_GRAPH_APP_ID = '361929ce-63d3-47c8-8490-0e24d6c05f01';
const MS_ACCESS_POLICY_SCRIPT = `# Run as a Teams administrator (Teams PowerShell module)
Connect-MicrosoftTeams
New-CsApplicationAccessPolicy -Identity "KaziPeople-Meeting-Links" \`
  -AppIds "${KAZIPEOPLE_GRAPH_APP_ID}" \`
  -Description "Allow KaziPeople to create Teams meetings"
# Allow for everyone in the organization:
Grant-CsApplicationAccessPolicy -PolicyName "KaziPeople-Meeting-Links" -Global`;

export default CalendarSyncSettingsCard;
